import { Button } from "@mantine/core";
import type React from "react";
import type { TrainerRequest } from "../../../types/portal";
import { getStatusBadgeClasses } from "../../../utils/status";
import EmptyState from "../../shared/EmptyState";
import LoadingState from "../../shared/LoadingState";

interface TrainerRequestsTabProps {
  trainerRequests: TrainerRequest[];
  requestsLoading: boolean;
  acceptingRequests: boolean;
  toggleLoading: boolean;
  onRefresh: () => void;
  onAcceptingRequestsToggle: () => void;
  onAcceptSession: (requestId: string) => void;
  onDeclineSession: (requestId: string) => void;
  onJoinSession: (requestId: string) => void;
}

const STATUS_LABELS: Record<string, string> = {
  pending: "Awaiting Your Response",
  awaiting_payment: "Waiting for Client Payment",
  accepted: "Ready to Join",
  active: "In Progress",
  declined: "Declined",
  cancelled: "Cancelled",
};

const getStatusLabel = (status: string): string =>
  STATUS_LABELS[status] || (status ? status.charAt(0).toUpperCase() + status.slice(1) : "Unknown");

const TrainerRequestsTab: React.FC<TrainerRequestsTabProps> = ({
  trainerRequests,
  requestsLoading,
  acceptingRequests,
  toggleLoading,
  onRefresh,
  onAcceptingRequestsToggle,
  onAcceptSession,
  onDeclineSession,
  onJoinSession,
}) => (
  <div>
    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-[30px] gap-3">
      <h2 className="text-xl sm:text-2xl text-foreground m-0 mb-0 sm:mb-5">
        Active Training Requests
      </h2>
      <div className="flex flex-wrap gap-4 items-center">
        <Button
          onClick={onRefresh}
          disabled={requestsLoading}
          className={`!px-4 !py-2 !bg-muted !text-muted-foreground !border !border-border !rounded-md !text-[0.9rem] !font-medium hover:!bg-border ${requestsLoading ? "!bg-border !cursor-wait !opacity-70" : ""}`}
        >
          {requestsLoading ? "Refreshing..." : "Refresh"}
        </Button>
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2.5 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={acceptingRequests}
              onChange={onAcceptingRequestsToggle}
              disabled={toggleLoading}
              className="hidden"
            />
            <span
              className={`relative w-[60px] h-[30px] rounded-[30px] transition-all duration-300 cursor-pointer ${acceptingRequests ? "bg-success" : "bg-muted"} ${toggleLoading ? "opacity-60 cursor-not-allowed" : ""}`}
            >
              <span
                className={`absolute top-[3px] left-[3px] w-6 h-6 bg-white rounded-full transition-all duration-300 shadow-md ${acceptingRequests ? "translate-x-[30px]" : "translate-x-0"}`}
              ></span>
            </span>
            <span className="text-base font-medium text-foreground min-w-[150px]">
              {toggleLoading
                ? "Updating..."
                : acceptingRequests
                  ? "Accepting Requests"
                  : "Not Accepting Requests"}
            </span>
          </label>
        </div>
      </div>
    </div>
    <div className="flex flex-col gap-5">
      {acceptingRequests ? (
        <div>
          <h3 className="text-[1.3rem] text-foreground mb-6 font-semibold border-b-2 border-primary pb-3 tracking-tight">
            Session Requests
          </h3>
          {requestsLoading ? (
            <LoadingState message="Loading requests..." />
          ) : trainerRequests.length === 0 ? (
            <EmptyState
              icon="No Requests"
              title="No Session Requests"
              description="You don't have any session requests yet. Clients will be able to request sessions with you while you're accepting requests."
            />
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-[repeat(auto-fill,minmax(280px,1fr))] gap-4 max-h-[600px] overflow-y-auto pr-2 mt-5">
              {trainerRequests
                .filter((req: TrainerRequest) => req.status !== "completed")
                .map((request: TrainerRequest, index: number) => (
                  <div
                    key={request.id || index}
                    className="bg-muted rounded-xl p-5 border border-border shadow-md transition-all duration-200 relative min-h-[280px] flex flex-col"
                  >
                    <div className="flex justify-between items-center mb-3">
                      <div className="text-2xl font-bold text-primary">#{index + 1}</div>
                      <div className="text-xs text-muted-foreground font-medium">
                        {new Date(request.requested_at).toLocaleDateString()}
                      </div>
                    </div>

                    <div className="mb-3">
                      <span
                        className={`px-2.5 py-1 rounded-xl text-xs font-semibold uppercase tracking-wider ${getStatusBadgeClasses(request.status)}`}
                      >
                        {getStatusLabel(request.status)}
                      </span>
                    </div>

                    <div className="flex-1 flex flex-col gap-2">
                      <div className="text-base text-foreground mb-2">
                        <strong>{request.client_username || "Unknown Client"}</strong>
                      </div>

                      <div className="flex flex-col gap-1">
                        <div className="text-sm text-muted-foreground font-medium">
                          {request.pet_name || "Unknown Pet"}
                        </div>
                        <div className="text-xs text-muted-foreground italic">
                          {request.pet_breed && `${request.pet_breed}`}
                          {request.pet_age && ` \u2022 ${request.pet_age} years old`}
                        </div>
                      </div>

                      <div className="text-xs text-muted-foreground mt-2 overflow-hidden text-ellipsis">
                        {request.explanation || "No explanation provided"}
                      </div>
                      {request.duration_minutes && (
                        <div className="text-xs text-muted-foreground mt-2 overflow-hidden text-ellipsis">
                          <strong>Duration:</strong> {request.duration_minutes} minutes
                        </div>
                      )}
                      {request.total_price !== undefined && request.total_price !== null && (
                        <div className="text-xs text-muted-foreground mt-2 overflow-hidden text-ellipsis">
                          <strong>Session Cost:</strong> $
                          {Number(request.total_price || 0).toFixed(2)}
                        </div>
                      )}
                    </div>

                    <div className="flex gap-2 mt-3 flex-wrap">
                      {request.status === "pending" && (
                        <>
                          <Button
                            color="green"
                            radius="md"
                            className="!flex-1"
                            onClick={() => onAcceptSession(request.id)}
                          >
                            Accept
                          </Button>
                          <Button
                            color="red"
                            radius="md"
                            className="!flex-1"
                            onClick={() => onDeclineSession(request.id)}
                          >
                            Decline
                          </Button>
                        </>
                      )}
                      {(request.status === "accepted" ||
                        request.status === "active" ||
                        (request.status === "awaiting_payment" &&
                          request.payment_status === "completed")) && (
                        <Button
                          radius="md"
                          className="!flex-1"
                          onClick={() => onJoinSession(request.id)}
                        >
                          {request.status === "active" ? "Rejoin Session" : "Join Session"}
                        </Button>
                      )}
                      {request.status === "awaiting_payment" &&
                        request.payment_status !== "completed" && (
                          <span className="text-muted-foreground text-xs italic px-4 py-2">
                            Awaiting client payment
                          </span>
                        )}
                      {request.status === "declined" && (
                        <span className="text-muted-foreground text-xs italic px-4 py-2">
                          Declined
                        </span>
                      )}
                      {request.status === "cancelled" && (
                        <span className="text-muted-foreground text-xs italic px-4 py-2">
                          Cancelled
                        </span>
                      )}
                    </div>
                  </div>
                ))}
            </div>
          )}
        </div>
      ) : (
        <EmptyState
          title="Not Accepting Requests"
          description={
            'Turn on the "Accepting Requests" toggle above to start receiving session requests from clients.'
          }
        />
      )}
    </div>
  </div>
);

export default TrainerRequestsTab;
